/**
 * Who is signed in: the Firebase identity, and the Grow account behind it.
 *
 * Signing in is by email and password, Google (a button, or One Tap), or
 * Facebook. Google and Facebook open a window in a browser tab and go by
 * redirect in the installed app, which has no window to open. Firebase's
 * sign-in pages are served from the app's own address (config/firebase,
 * vite.config's /__ proxy), so Google and Facebook name Garden Gameplan, and
 * the redirect comes back to the same site that started it.
 *
 * An email that already has an account by another way (Google, say, when
 * Facebook was pressed) is told to sign in that way once; the Facebook sign-in
 * is kept and joined to the account when they do (pending link), so either
 * works after.
 *
 * The Grow account (mysqlUser) is read with GET /users/firebase/:uid as soon
 * as Firebase names someone. That one request goes through a bare axios
 * call with the token set by hand rather than through apiClient, so it
 * carries no X-Grow-Garden header (the account is the person's own). A 404
 * is not an error: signed in, with no account yet, which CompleteSignup
 * finishes; a 403 ACCOUNT_SUSPENDED signs them out with a sentence. Any
 * other failure (a 500, no connection) is not "no account": it is reported
 * to Sentry and accountFailed is set, and Layout shows the standard failure
 * page instead of the sign-up path (D31, lib/serverFailure). A 429 (the
 * server's limit, tripped by a few quick page loads) is waited out and read
 * again, up to ACCOUNT_READ_TRIES, before it counts as a failure; a failure
 * that was the limit on every try sets accountLimited too, so the failure
 * page says the limit's own sentence, with the support address and no "try
 * again" (B24).
 *
 * Nothing under AuthProvider is drawn until the first answer is in
 * (loading), and every change of account clears the React Query cache and
 * the toasts, since no query key names an account. With VITE_USE_MOCK_AUTH
 * a fake Firebase user stands in for VITE_DEV_FIREBASE_UID, whose real
 * account is still loaded, read through apiClient (which sends the dev header)
 * and treated as current without Firebase (B54).
 *
 * Used through shared/hooks/useAuth by nearly every screen; App wraps the
 * app in AuthProvider inside the QueryClientProvider (it needs the client).
 *
 * @module AuthContext
 */

import React, { useEffect, useState } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  linkWithCredential,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthCredential,
  type AuthProvider as SignInProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  User,
  UserCredential,
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import { AuthContext, type AuthContextType } from './AuthContextDefinition';
import type { MySQLUser } from '../types/user';
import { getUserByFirebaseId, SIGNUP_NAME_KEY } from '../api/users';
import { isIdentityCurrent, type AccountRead } from './accountRead';
import { forgetChosenGarden } from '../api/gardenMembers';
import { forgetViewAs } from '../api/viewAs';
import { authErrorMessage, isCancellation, throwReadable } from './authErrors';
import { clearToasts } from '@/shared/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { debugLog } from '@/config/config';
import { setSentryUser } from '@/shared/lib/sentry';
import { isRateLimited, reportServerFailure, referenceOf } from '@/shared/lib/serverFailure';
import { t } from '@/shared/i18n/i18nSetup';
import { PENDING_SIGN_IN_LINK_SESSION_KEY, STANDALONE_DISPLAY_MEDIA_QUERY, ACCOUNT_READ_TRIES, ACCOUNT_READ_RETRY_MS, ACCOUNT_READ_RETRY_MAX_MS } from '@/config/tunables/ui';

/** The API's address, read the way apiClient reads it. */
const BACKEND_URL = import.meta.env.VITE_API_URL;
/** A fake signed-in user for local work, with the real account behind its UID. */
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';
/** The Firebase UID mock auth signs in as; its Grow account is loaded for real. */
const DEV_FIREBASE_UID = import.meta.env.VITE_DEV_FIREBASE_UID;

// Enhanced debugging for environment variables
debugLog('AuthContext', 'Auth Context - Environment Variables:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  VITE_USE_MOCK_AUTH: import.meta.env.VITE_USE_MOCK_AUTH,
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY ? 'Set (hidden)' : 'Not set',
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  DEV: import.meta.env.DEV,
  MODE: import.meta.env.MODE,
});

debugLog('AuthContext', 'Auth Context - Using backend URL:', BACKEND_URL);
debugLog('AuthContext', 'Auth Context - Mock Auth Enabled:', USE_MOCK_AUTH);
debugLog('AuthContext', 'Auth Context - Firebase Auth Object:', auth ? 'Initialized' : 'Not initialized');
debugLog('AuthContext', 'Auth Context - Using Firebase UID for development:', DEV_FIREBASE_UID);

/**
 * Keeps the sign-in Firebase refused because the email already has an
 * account by another way, to join it to that account once they sign in.
 *
 * @param error - What the sign-in threw.
 * @returns True when it was that refusal.
 */
function keepPendingLink(error: unknown): boolean {
  if ((error as { code?: string })?.code !== 'auth/account-exists-with-different-credential') return false;
  const credential = FacebookAuthProvider.credentialFromError(error as Parameters<typeof FacebookAuthProvider.credentialFromError>[0]);
  if (credential) {
    try { window.sessionStorage.setItem(PENDING_SIGN_IN_LINK_SESSION_KEY, JSON.stringify(credential.toJSON())); } catch { /* private mode */ }
  }
  return true;
}

/**
 * Joins a kept sign-in (keepPendingLink) to the account just signed into, so
 * either way works from now on. Best effort: a failure only means the gardener
 * keeps signing in the way they just did.
 *
 * @param user - Who just signed in.
 */
async function linkPending(user: User): Promise<void> {
  let kept: string | null = null;
  try {
    kept = window.sessionStorage.getItem(PENDING_SIGN_IN_LINK_SESSION_KEY);
    window.sessionStorage.removeItem(PENDING_SIGN_IN_LINK_SESSION_KEY);
  } catch { /* private mode */ }
  if (!kept) return;
  try {
    const credential = OAuthCredential.fromJSON(kept);
    if (credential) await linkWithCredential(user, credential);
    debugLog('AuthContext', 'Joined the kept sign-in to the account');
  } catch (error) {
    console.error('Could not join the kept sign-in to the account:', error);
  }
}


/**
 * How long the server asks to wait before the next read, from a 429's
 * Retry-After (seconds), within ACCOUNT_READ_RETRY_MAX_MS; else
 * ACCOUNT_READ_RETRY_MS.
 *
 * @param error - The 429.
 * @returns Milliseconds.
 */
function retryAfterMs(error: unknown): number {
  const header = axios.isAxiosError(error) ? Number(error.response?.headers?.['retry-after']) : NaN;
  const asked = Number.isFinite(header) && header > 0 ? header * 1000 : ACCOUNT_READ_RETRY_MS;
  return Math.min(asked, ACCOUNT_READ_RETRY_MAX_MS);
}

/**
 * GET /users/firebase/:uid, read again after a wait when the server answers
 * 429 (too many requests), up to ACCOUNT_READ_TRIES; any other answer, or the
 * last 429, as it came. Stops early when the identity has changed.
 *
 * @param uid - The Firebase identity.
 * @param token - Its ID token.
 * @param stillCurrent - Whether the identity is still the one signed in.
 * @returns The response.
 */
async function readAccountWaitingOutLimit(uid: string, token: string, stillCurrent: () => boolean) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await axios.get(`${BACKEND_URL}/users/firebase/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      const limited = axios.isAxiosError(error) && error.response?.status === 429;
      if (!limited || attempt >= ACCOUNT_READ_TRIES || !stillCurrent()) throw error;
      await new Promise((resolve) => setTimeout(resolve, retryAfterMs(error)));
    }
  }
}

/**
 * Holds who is signed in and offers the ways to sign in and out. Nothing
 * below it is drawn until the first answer on who is signed in has come back,
 * so no page renders for a moment as nobody.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [mysqlUser, setMysqlUser] = useState<MySQLUser | null>(null);
  // The account could not be read (not "there is none"): the failure page, not sign-up.
  const [accountFailed, setAccountFailed] = useState(false);
  // That failure was the server's request limit on every try (B24): its own sentence on the failure page.
  const [accountLimited, setAccountLimited] = useState(false);
  // The short reference code of that failure's Sentry report (E3), for the failure page; undefined when Sentry is off.
  const [accountReference, setAccountReference] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  // Why a sign-in that went to Google or Facebook and back did not finish.
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Who was signed in when something broke. The id only — never the email.
  useEffect(() => {
    setSentryUser(mysqlUser?.user_id ?? null);
  }, [mysqlUser]);

  /**
   * Loads the Grow account behind a Firebase identity, if there is one.
   *
   * There may legitimately not be. Firebase sign-up and the row in the user
   * table are two separate things, and between them sits the moment a person
   * has proved who they are and has not yet told us where they garden. That
   * is not an error; leaving mysqlUser null is how the app knows to ask.
   *
   * This used to try to paper over that by silently POSTing an account into
   * existence — twice, in two nearly identical branches, to an endpoint that
   * did not exist. Had it worked it would have been worse than the 404: an
   * account with no postal code cannot produce a planting date, a frost
   * warning or a weather reading, so it would have created exactly the broken
   * accounts the signup form now exists to prevent. (Accounts are made now by
   * CompleteSignup, which asks for the postcode afterwards, on Today.)
   *
   * An answer that arrives after its identity has signed out, or after
   * someone else has signed in, is dropped: it used to put the last
   * person's account back under the next one.
   *
   * A failure that is not a 404 (a 500, no connection, a token Firebase
   * could not give) used to leave mysqlUser null too, which Layout read as
   * "no account yet" and sent an existing gardener down the sign-up path,
   * where making the account could fail again and say so. It is reported
   * now, and accountFailed set, so Layout says the standard failure instead.
   *
   * Under mock auth the read goes through getUserByFirebaseId (apiClient,
   * which sends the dev header the mock server needs) and the mock uid counts
   * as current, since Firebase never signs in there (B54).
   *
   * @param firebaseUser - The identity to read the account of.
   * @returns 'ok' when an account was read and set; 'none' when the server
   *   answered that there is none (a 404, or an empty answer); 'failed' when
   *   the read failed (reported here, or said on the sign-in page for a
   *   suspended account) or its answer was dropped.
   */
  const fetchMysqlUser = async (firebaseUser: User): Promise<AccountRead> => {
    /** Whether this identity is still the one signed in. */
    const stillCurrent = (): boolean => isIdentityCurrent(USE_MOCK_AUTH, firebaseUser.uid, auth.currentUser?.uid);
    try {
      let account: MySQLUser | null | undefined;
      if (USE_MOCK_AUTH) {
        account = await getUserByFirebaseId(firebaseUser.uid);
      } else {
        const token = await firebaseUser.getIdToken();
        const response = await readAccountWaitingOutLimit(firebaseUser.uid, token, stillCurrent);
        account = response.data?.user;
      }
      if (!stillCurrent()) return 'failed';

      setAccountFailed(false);
      setAccountLimited(false);
      if (!account) {
        debugLog('AuthContext', 'No Grow account for this Firebase user yet - signup will finish it');
        setMysqlUser(null);
        return 'none';
      }

      setMysqlUser(account);
      return 'ok';
    } catch (error) {
      if (!stillCurrent()) return 'failed';
      // A suspended account (the owner's Gardeners tab) cannot sign in: said on
      // the sign-in page, and signed out of Firebase so it does not look set up.
      if (axios.isAxiosError(error) && error.response?.status === 403
        && (error.response?.data?.code === 'ACCOUNT_SUSPENDED' || error.response?.data?.error === 'ACCOUNT_SUSPENDED')) {
        setRedirectError(t('serverErrors.ACCOUNT_SUSPENDED'));
        setAccountFailed(false);
        setAccountLimited(false);
        setMysqlUser(null);
        await signOut(auth).catch(() => {});
        return 'failed';
      }
      // A 404 is the ordinary "signed up, not set up yet" case and needs no
      // noise. Anything else is a failure to read an account that may well
      // exist: reported, and the failure page rather than sign-up.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        debugLog('AuthContext', 'No Grow account for this Firebase user yet - signup will finish it');
        setAccountFailed(false);
        setAccountLimited(false);
        setMysqlUser(null);
        return 'none';
      } else {
        console.warn('Could not load the Grow account:', error);
        reportServerFailure(error, 'account');
        setAccountReference(referenceOf(error));
        setAccountFailed(true);
        setAccountLimited(isRateLimited(error));
      }
      setMysqlUser(null);
      return 'failed';
    }
  };

  // Handle auth state changes and redirect results
  useEffect(() => {
    debugLog('AuthContext', 'Setting up Firebase auth state and redirect handlers');

    // If using mock auth in development, create a fake user
    if (USE_MOCK_AUTH) {
      debugLog('AuthContext', 'MOCK AUTH: Creating mock Firebase user');
      const mockUser = {
        uid: DEV_FIREBASE_UID,
        email: 'mock@example.com',
        displayName: 'Mock User',
        emailVerified: true,
        getIdToken: async () => 'mock-token-123',
      } as unknown as User;

      setCurrentUser(mockUser);

      // Load the real MySQL record for this UID instead of fabricating one.
      // The hardcoded stub that used to live here carried no postal_code, so
      // hasProfileZip was false for every dev session: the "add a ZIP code"
      // banner showed permanently and the GGP planting-windows path could
      // never be exercised locally. A lookup that fails other than with a
      // 404 shows the failure page, as it does for a real sign-in, so that
      // page can be walked locally (it fell back to a stub user 1 before).
      //
      // A 404 is not the backend being down; it is the backend saying this
      // identity has no account yet — the same answer the real path gets for
      // a brand-new sign-in, which it turns into the finish-signing-up screen.
      // The stub used to swallow that and pose as user 1, so a mock session
      // could never walk the path a stranger walks. Now it can.
      (async () => {
        try {
          const realUser = await getUserByFirebaseId(DEV_FIREBASE_UID);
          debugLog('AuthContext', 'MOCK AUTH: loaded real MySQL user', realUser);
          setMysqlUser(realUser);
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 404) {
            debugLog('AuthContext', 'MOCK AUTH: no Grow account for this UID yet - signup will finish it');
            setMysqlUser(null);
            return;
          }
          console.warn('MOCK AUTH: could not load the Grow account:', err);
          reportServerFailure(err, 'account');
          setAccountReference(referenceOf(err));
          setAccountFailed(true);
          setAccountLimited(isRateLimited(err));
          setMysqlUser(null);
        } finally {
          setLoading(false);
        }
      })();
      return () => {};
    }

    let authStateUnsubscribe: (() => void) | undefined;
    /*
     * Set by the cleanup. The listener is subscribed only after
     * getRedirectResult resolves, which can be after the cleanup has already
     * run (StrictMode mounts twice in development); without this the late
     * subscription was never unsubscribed, and every auth change was then
     * handled twice.
     */
    let disposed = false;

    /**
     * Listens for every change of who is signed in, here or in another tab:
     * a user is kept with their Grow account read; none clears all of it.
     * Written once, for the path after a redirect result and the path where
     * the redirect failed.
     */
    const listenForSignIn = (): void => {
      authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
        debugLog('AuthContext', 'Auth state changed:', user ? `User: ${user.uid}` : 'No user');
        setCurrentUser(user);
        if (user) {
          void linkPending(user);
          await fetchMysqlUser(user);
        } else {
          // Signed out, here or in another tab: the garden being tended was theirs to choose.
          forgetChosenGarden();
      forgetViewAs();
          setMysqlUser(null);
          setAccountFailed(false);
          setAccountLimited(false);
        }

        // Only set loading to false after we've handled auth state
        setLoading(false);
      });
    };

    /**
     * Picks up a sign-in coming back from a redirect, then listens for every
     * later change of who is signed in.
     */
    const handleInitialAuth = async () => {
      try {
        debugLog('AuthContext', 'Checking for redirect result and setting up auth state listener...');

        // Keep loading true until we've fully determined the auth state
        setLoading(true);

        // Check for redirect result first
        const result = await getRedirectResult(auth);
        if (disposed) return;
        if (result?.user) {
          debugLog('AuthContext', 'Found redirect result, user:', result.user.uid);
          setCurrentUser(result.user);
          await fetchMysqlUser(result.user);
        }

        // Then listen for every later change
        listenForSignIn();
      } catch (error) {
        console.error('Error in auth initialization:', error);
        if (disposed) return;
        // Back from Google or Facebook without a sign-in: said on the sign-in
        // page (redirectError), and the listener still set up below.
        if (!isCancellation(error)) {
          keepPendingLink(error);
          setRedirectError(authErrorMessage(error));
        }
        listenForSignIn();
      }
    };

    void handleInitialAuth();

    return () => {
      disposed = true;
      if (authStateUnsubscribe) {
        debugLog('AuthContext', 'Cleaning up auth state listener');
        authStateUnsubscribe();
      }
    };
  }, []);

  /**
   * Signs in through Google or Facebook.
   *
   * Installed on a phone, the app has no browser window for a popup to open
   * in; the redirect goes there in the same window and comes back to
   * getRedirectResult above. In a browser tab the popup stays, since it keeps
   * the page where it was.
   *
   * @param provider - Google's or Facebook's, set up by the caller.
   * @param name - For the log.
   * @returns The sign-in, or null when it went by redirect or was backed out of.
   */
  const signInWithProvider = async (provider: SignInProvider, name: string): Promise<UserCredential | null> => {
    try {
      if (window.matchMedia?.(STANDALONE_DISPLAY_MEDIA_QUERY).matches) {
        debugLog('AuthContext', `Installed app: ${name} sign-in by redirect`);
        await signInWithRedirect(auth, provider);
        return null;
      }

      debugLog('AuthContext', `Initiating ${name} sign-in with popup`);
      const result = await signInWithPopup(auth, provider);
      debugLog('AuthContext', `${name} sign in successful`, { uid: result.user.uid });

      // Explicitly set the current user and fetch MySQL user data
      setCurrentUser(result.user);
      await fetchMysqlUser(result.user);
      return result;
    } catch (error) {
      console.error(`Error signing in with ${name}:`, error);
      // Closing the window is a decision, not a fault. Everything else is
      // worth saying out loud.
      if (isCancellation(error)) return null;
      keepPendingLink(error);
      throwReadable(error);
    }
  };

  /**
   * Google sign-in: the account only, choosing which each time. It asked for
   * the Google Photos library too (photoslibrary.readonly), a permission
   * Google retired in March 2025; Google Photos asks for its own now, when it
   * is used (GooglePhotosService).
   */
  const signInWithGoogle = async (): Promise<UserCredential | null> => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return signInWithProvider(provider, 'Google');
  };

  /**
   * Facebook sign-in, asking for the email address the account is keyed on.
   *
   * @returns The sign-in, or null (redirect, or backed out of).
   */
  const signInWithFacebook = async (): Promise<UserCredential | null> => {
    const provider = new FacebookAuthProvider();
    provider.addScope('email');
    return signInWithProvider(provider, 'Facebook');
  };

  /**
   * Signs in with the token Google One Tap hands back (GoogleOneTap): no
   * window, the account chosen on the page.
   *
   * @param idToken - Google's ID token for the chosen account.
   * @returns The sign-in.
   */
  const signInWithGoogleIdToken = async (idToken: string): Promise<UserCredential | null> => {
    try {
      const result = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
      setCurrentUser(result.user);
      await fetchMysqlUser(result.user);
      return result;
    } catch (error) {
      console.error('Error signing in with Google One Tap:', error);
      keepPendingLink(error);
      throwReadable(error);
    }
  };

  /**
   * Signs in with an email address and password; throws a readable error.
   *
   * @param email - The address.
   * @param password - The password, passed straight to Firebase.
   * @returns The sign-in.
   */
  const signInWithEmail = async (email: string, password: string): Promise<UserCredential | null> => {
    try {
      debugLog('AuthContext', 'Signing in with email:', email);

      if (USE_MOCK_AUTH) {
        // Silently returning null here made the form look broken. It is not
        // reachable in mock mode today, but saying why costs one line and the
        // alternative is another silent no-op.
        throw new Error('Mock auth is on: sign-in is simulated, not performed');
      }

      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in with email/password:', error);
      throwReadable(error);
    }
  };

  /**
   * Makes a Firebase account with email and password, and puts the name on
   * it. The name is also kept for the Grow account (SIGNUP_NAME_KEY), which
   * CompleteSignup then makes without asking again.
   *
   * @param email - The address.
   * @param password - The password, passed straight to Firebase.
   * @param displayName - The name typed on the form.
   * @returns The new Firebase sign-in.
   */
  const signUpWithEmail = async (email: string, password: string, displayName: string): Promise<UserCredential | null> => {
    try {
      debugLog('AuthContext', 'Creating new account with email:', email);

      if (USE_MOCK_AUTH) {
        throw new Error('Mock auth is on: turn off VITE_USE_MOCK_AUTH to create a real account');
      }

      // Kept for the Grow account, made next (CompleteSignup) without asking
      // again: the sign-in state can arrive before the name is on the user.
      try { window.sessionStorage.setItem(SIGNUP_NAME_KEY, displayName.trim()); } catch { /* private mode */ }
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Update the user's display name
      if (result.user) {
        debugLog('AuthContext', 'Setting display name for new user:', displayName);
        await updateProfile(result.user, { displayName });
      }
      return result;
    } catch (error) {
      console.error('Error signing up with email/password:', error);
      throwReadable(error);
    }
  };

  /**
   * Firebase's own reset mail, used only when the server could not send ours.
   *
   * Needs no admin credentials — it is the web SDK talking to Firebase
   * directly — which is exactly why it works when the server's service
   * account does not. The mail is Firebase-branded rather than ours; an
   * unbranded email a gardener can act on beats a branded one that never
   * arrives.
   *
   * Never throws. An unknown address is not something to report here.
   *
   * @param email - The address to send it to.
   */
  const sendResetEmailDirect = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Fallback password reset failed:', error);
    }
  };

  /** Signs out, and forgets everything cached for the account being left. */
  const logout = async (): Promise<void> => {
    try {
      debugLog('AuthContext', 'Logging out user');

      // The toast store is module-level and outlives every component. Anything
      // still in it belongs to the session being left.
      clearToasts();

      // So does the query cache, and its keys are not scoped to an account:
      // 'locations', 'plants', 'notes' and the rest are the same key whoever
      // is signed in, with a five minute stale time. Left alone, the next
      // person to sign in on this tab is served the last one's garden.
      queryClient.clear();

      // And the garden this device was tending: the next person to sign in
      // here would send it with every request and be refused on every page.
      forgetChosenGarden();
      forgetViewAs();

      if (USE_MOCK_AUTH) {
        debugLog('AuthContext', 'MOCK AUTH: Simulating logout');
        setCurrentUser(null);
        setMysqlUser(null);
        setAccountFailed(false);
        setAccountLimited(false);
        return;
      }

      await signOut(auth);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  /*
   * Throw away cached data whenever the account changes.
   *
   * Logging out covers the ordinary case, but not all of them: signing up
   * straight after deleting an account never passes through logout's clear
   * on a fresh identity, so the new garden was served the deleted one's
   * locations — the seasonal picture appeared only after a reload, five
   * minutes of stale time later. Keyed on the account id, so a refresh of the
   * same account keeps its cache.
   */
  const lastAccountId = React.useRef<number | null>(null);
  useEffect(() => {
    const id = mysqlUser?.user_id ?? null;
    if (lastAccountId.current !== null && lastAccountId.current !== id) {
      queryClient.clear();
    }
    lastAccountId.current = id;
  }, [mysqlUser?.user_id, queryClient]);

  /**
   * Re-reads the Grow account from the server.
   *
   * Used by the signup screen once it has created one. updateMysqlUser cannot
   * do this job: it merges into existing state and refuses when there is
   * none, which is precisely the situation here.
   *
   * @returns What the read found (AccountRead): CompleteSignup reports only
   *   'none', and shows the failure state rather than waiting for an account
   *   that will not arrive.
   */
  const refreshMysqlUser = async (): Promise<AccountRead> => {
    if (!currentUser) return 'failed';
    return fetchMysqlUser(currentUser);
  };

  /**
   * Merges profile fields already saved on the server into the account held
   * here (no second PUT).
   *
   * Merged into the latest state rather than the one this render saw: two
   * updates in a row (a name, then a photo) each spread the same stale copy,
   * and the second undid the first.
   *
   * @param updates - The fields as saved.
   */
  const updateMysqlUser = (updates: Partial<MySQLUser>) => {
    if (!mysqlUser) {
      console.warn('Cannot update MySQL user - no authenticated user');
      return;
    }
    debugLog('AuthContext', 'Updating MySQL user state:', updates);
    setMysqlUser((latest) => (latest ? { ...latest, ...updates } : latest));
  };

  const value: AuthContextType = {
    currentUser,
    mysqlUser,
    accountFailed,
    accountLimited,
    accountReference,
    loading,
    signInWithGoogle,
    signInWithFacebook,
    signInWithGoogleIdToken,
    redirectError,
    signInWithEmail,
    signUpWithEmail,
    logout,
    updateMysqlUser,
    refreshMysqlUser,
    sendResetEmailDirect,
  };

  debugLog('AuthContext', 'Auth context value:', {
    hasUser: !!currentUser,
    hasMysqlUser: !!mysqlUser,
    isLoading: loading
  });

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};